import {
  DeesElement,
  customElement,
  property,
  state,
  type TemplateResult,
} from '@design.estate/dees-element';

import * as domtools from '@design.estate/dees-domtools';
import { demoFunc } from './demo.js';
import { chartAreaStyles } from './styles.js';
import { renderChartArea } from './template.js';
import {
  haveSameSeriesLayout,
  planRealtimeChartUpdates,
  type IRealtimeChartPoint,
} from './realtime-updates.js';
import { getEchartsSeriesColors, hexToRgba } from '../dees-chart-echarts-theme.js';

import type { IChartApi, ISeriesApi, UTCTimestamp, MouseEventParams } from 'lightweight-charts';
import { DeesServiceLibLoader, type ILightweightChartsBundle } from '../../../services/index.js';
import '../../00group-layout/dees-tile/dees-tile.js';

export type ChartSeriesConfig = {
  name?: string;
  /** Explicit series color (hex or hsl). Falls back to the shared catalog palette by index. */
  color?: string;
  data: Array<{ x: any; y: number }>;
}[];

export type TChartLegendStat = 'latest' | 'min' | 'max' | 'avg';
export type TChartPriceLine = 'avg' | 'max';

export const CHART_LEGEND_STATS: TChartLegendStat[] = ['latest', 'min', 'max', 'avg'];

export interface IChartSelectedRange {
  from: number;
  to: number;
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-chart-area': DeesChartArea;
  }
}

@customElement('dees-chart-area')
export class DeesChartArea extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Chart'];

  @state()
  accessor chart: IChartApi | null = null;

  @state()
  accessor seriesStats: Array<{ name: string; latest: number; min: number; max: number; avg: number; color: string }> = [];

  @state()
  accessor isFullPage: boolean = false;

  @property()
  accessor label: string = 'Untitled Chart';

  @property({ type: Array })
  accessor series: ChartSeriesConfig = [];

  get chartSeries(): ChartSeriesConfig {
    return this.internalChartData.length > 0 ? this.internalChartData : this.series;
  }

  @property({ attribute: false })
  accessor yAxisFormatter: (value: number) => string = (val) => `${val} Mbps`;

  @property({ type: Number })
  accessor rollingWindow: number = 0;

  @property({ type: Boolean })
  accessor realtimeMode: boolean = false;

  @property({ type: String })
  accessor yAxisScaling: 'fixed' | 'dynamic' | 'percentage' = 'dynamic';

  @property({ type: Number })
  accessor yAxisMax: number = 100;

  @property({ type: Number })
  accessor autoScrollInterval: number = 1000;

  /**
   * Which per-series stats appear in the footer legend, in the given order.
   * Restrict (e.g. ['latest']) or empty ([] → color dot + name only) so the
   * legend fits smaller tiles. Default shows all stats.
   */
  @property({ type: Array })
  accessor legendStats: TChartLegendStat[] = [...CHART_LEGEND_STATS];

  /** Hide the footer legend entirely. */
  @property({ type: Boolean })
  accessor hideLegend: boolean = false;

  /**
   * Which reference lines are drawn on the chart per series. The 'max' line
   * keeps its existing rule of only appearing when max exceeds avg by >10%.
   */
  @property({ type: Array })
  accessor chartLines: TChartPriceLine[] = ['avg', 'max'];


  @property({ type: Boolean })
  accessor rangeSelectionEnabled = false;

  @property({ attribute: false })
  accessor selectedRange: IChartSelectedRange | null = null;
  private internalChartData: ChartSeriesConfig = [];
  private autoScrollTimer: number | null = null;
  private lcBundle: ILightweightChartsBundle | null = null;
  private seriesApis: Map<string, ISeriesApi<any>> = new Map();
  private priceLines: Map<string, any[]> = new Map();
  private tooltipEl: HTMLDivElement | null = null;
  private crosshairActive = false;
  private pendingRealtimePrune = false;
  private crosshairMoveHandler: ((param: MouseEventParams) => void) | null = null;
  private chartContainer: HTMLDivElement | null = null;
  private rangeOverlay: HTMLDivElement | null = null;
  private rangeResizeObserver: ResizeObserver | null = null;
  private rangeDrag: { pointerId: number; startX: number; startMs: number } | null = null;
  private readonly rangePointerDownHandler = (event: PointerEvent): void => {
    if (!this.rangeSelectionEnabled || event.button !== 0 || !this.chartContainer) return;
    const x = this.getRangePointerX(event);
    const timeMs = this.coordinateToEpochMs(x);
    if (timeMs === null) return;
    event.preventDefault();
    this.rangeDrag = { pointerId: event.pointerId, startX: x, startMs: timeMs };
    try { this.chartContainer.setPointerCapture(event.pointerId); } catch { /* synthetic event */ }
    window.addEventListener('keydown', this.rangeKeydownHandler);
    this.paintRangeOverlay(x, x);
  };
  private readonly rangePointerMoveHandler = (event: PointerEvent): void => {
    if (!this.rangeDrag || event.pointerId !== this.rangeDrag.pointerId) return;
    event.preventDefault();
    this.paintRangeOverlay(this.rangeDrag.startX, this.getRangePointerX(event));
  };
  private readonly rangePointerUpHandler = (event: PointerEvent): void => {
    const drag = this.rangeDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const endX = this.getRangePointerX(event);
    const endMs = this.coordinateToEpochMs(endX);
    this.finishRangeDrag(event.pointerId);
    if (endMs === null || Math.abs(endX - drag.startX) < 4) {
      this.syncRangeOverlay();
      return;
    }
    const from = Math.min(drag.startMs, endMs);
    const to = Math.max(drag.startMs, endMs);
    if (!Number.isSafeInteger(from) || !Number.isSafeInteger(to) || from >= to) {
      this.syncRangeOverlay();
      return;
    }
    this.dispatchEvent(new CustomEvent<IChartSelectedRange>('range-change', {
      detail: { from, to },
      bubbles: true,
      composed: true,
    }));
    this.syncRangeOverlay();
  };
  private readonly rangePointerCancelHandler = (event: PointerEvent): void => {
    if (!this.rangeDrag || event.pointerId !== this.rangeDrag.pointerId) return;
    this.finishRangeDrag(event.pointerId);
    this.syncRangeOverlay();
  };
  private readonly rangeKeydownHandler = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || !this.rangeDrag) return;
    const pointerId = this.rangeDrag.pointerId;
    this.finishRangeDrag(pointerId);
    this.syncRangeOverlay();
  };
  private readonly rangeVisibleTimeChangeHandler = (): void => {
    this.syncRangeOverlay();
  };
  private readonly chartMouseLeaveHandler = () => {
    this.chart?.clearCrosshairPosition();
    this.handleCrosshairCleared();
  };

  constructor() {
    super();
    domtools.elementBasic.setup();
    this.registerGarbageFunction(async () => {
      this.stopAutoScroll();
      const chart = this.chart;
      const crosshairMoveHandler = this.crosshairMoveHandler;
      this.crosshairMoveHandler = null;
      this.chartContainer?.removeEventListener('mouseleave', this.chartMouseLeaveHandler);
      this.teardownRangeSelection();
      this.chart = null;
      this.rangeResizeObserver?.disconnect();
      this.rangeResizeObserver = null;
      this.chartContainer = null;
      this.tooltipEl = null;
      this.rangeOverlay = null;
      this.crosshairActive = false;
      this.pendingRealtimePrune = false;
      if (crosshairMoveHandler && chart) {
        try {
          chart.unsubscribeCrosshairMove(crosshairMoveHandler);
        } catch (error) {
          console.error('Error unsubscribing chart crosshair:', error);
        }
      }
      try {
        chart?.remove();
      } catch (error) {
        console.error('Error destroying chart:', error);
      } finally {
        this.seriesApis.clear();
        this.priceLines.clear();
      }
    });
  }

  public static styles = chartAreaStyles;

  public render(): TemplateResult {
    return renderChartArea(this);
  }

  // --- Helpers ---

  private getRangePointerX(event: PointerEvent): number {
    const rect = this.chartContainer!.getBoundingClientRect();
    return Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
  }

  private coordinateToEpochMs(coordinateArg: number): number | null {
    const time = this.chart?.timeScale().coordinateToTime(coordinateArg);
    if (typeof time !== 'number' || !Number.isFinite(time)) return null;
    const epochMs = Math.round(time * 1000);
    return Number.isSafeInteger(epochMs) && epochMs >= 0 ? epochMs : null;
  }

  private paintRangeOverlay(firstXArg: number, secondXArg: number): void {
    if (!this.rangeOverlay) return;
    const left = Math.min(firstXArg, secondXArg);
    const width = Math.abs(secondXArg - firstXArg);
    this.rangeOverlay.style.display = 'block';
    this.rangeOverlay.style.left = `${left}px`;
    this.rangeOverlay.style.width = `${Math.max(width, 1)}px`;
  }

  private finishRangeDrag(pointerIdArg: number): void {
    this.rangeDrag = null;
    window.removeEventListener('keydown', this.rangeKeydownHandler);
    try { this.chartContainer?.releasePointerCapture(pointerIdArg); } catch { /* already released */ }
  }

  private setupRangeSelection(): void {
    if (!this.chartContainer) return;
    this.rangeOverlay = this.chartContainer.querySelector('.rangeSelectionOverlay');
    this.chart?.timeScale().subscribeVisibleTimeRangeChange(this.rangeVisibleTimeChangeHandler);
    this.chartContainer.addEventListener('pointerdown', this.rangePointerDownHandler);
    this.chartContainer.addEventListener('pointermove', this.rangePointerMoveHandler);
    this.chartContainer.addEventListener('pointerup', this.rangePointerUpHandler);
    this.chartContainer.addEventListener('pointercancel', this.rangePointerCancelHandler);
    this.chartContainer.addEventListener('lostpointercapture', this.rangePointerCancelHandler);
    this.rangeResizeObserver = new ResizeObserver(() => this.syncRangeOverlay());
    this.rangeResizeObserver.observe(this.chartContainer);
    this.syncRangeOverlay();
  }

  private teardownRangeSelection(): void {
    if (!this.chartContainer) return;
    this.chart?.timeScale().unsubscribeVisibleTimeRangeChange(this.rangeVisibleTimeChangeHandler);
    this.chartContainer.removeEventListener('pointerdown', this.rangePointerDownHandler);
    this.chartContainer.removeEventListener('pointermove', this.rangePointerMoveHandler);
    this.chartContainer.removeEventListener('pointerup', this.rangePointerUpHandler);
    this.chartContainer.removeEventListener('pointercancel', this.rangePointerCancelHandler);
    this.chartContainer.removeEventListener('lostpointercapture', this.rangePointerCancelHandler);
    window.removeEventListener('keydown', this.rangeKeydownHandler);
    if (this.rangeDrag) {
      try { this.chartContainer.releasePointerCapture(this.rangeDrag.pointerId); } catch { /* no capture */ }
    }
    this.rangeDrag = null;
  }

  private syncRangeOverlay(): void {
    if (!this.rangeOverlay || !this.chart || !this.rangeSelectionEnabled || !this.selectedRange) {
      if (this.rangeOverlay) this.rangeOverlay.style.display = 'none';
      return;
    }
    const { from, to } = this.selectedRange;
    if (
      !Number.isSafeInteger(from)
      || !Number.isSafeInteger(to)
      || from < 0
      || to <= from
    ) {
      this.rangeOverlay.style.display = 'none';
      return;
    }
    const fromCoordinate = this.chart.timeScale().timeToCoordinate(
      Math.floor(from / 1000) as UTCTimestamp,
    );
    const toCoordinate = this.chart.timeScale().timeToCoordinate(
      Math.floor(to / 1000) as UTCTimestamp,
    );
    if (fromCoordinate === null || toCoordinate === null) {
      this.rangeOverlay.style.display = 'none';
      return;
    }
    this.paintRangeOverlay(fromCoordinate, toCoordinate);
  }

  private convertDataToLC(data: Array<{ x: any; y: number }>): Array<{ time: UTCTimestamp; value: number }> {
    const pointsByTime = new Map<number, { time: UTCTimestamp; value: number }>();
    for (const point of data) {
      const ms = typeof point.x === 'number' ? point.x : new Date(point.x).getTime();
      const time = Math.floor(ms / 1000);
      if (!Number.isFinite(time) || !Number.isFinite(point.y)) continue;
      // Lightweight Charts requires unique ordered timestamps. The latest
      // sample wins when multiple updates land in the same second.
      pointsByTime.set(time, { time: time as UTCTimestamp, value: point.y });
    }
    return [...pointsByTime.values()]
      .sort((a, b) => (a.time as number) - (b.time as number));
  }

  private getSeriesName(series: ChartSeriesConfig[number], index: number): string {
    return series.name || `series-${index}`;
  }

  private getSeriesKey(series: ChartSeriesConfig[number], index: number): string {
    return `${index}:${this.getSeriesName(series, index)}`;
  }

  private getCanonicalChartSeries(chartSeries: ChartSeriesConfig): ChartSeriesConfig {
    const cutoffTime = Date.now() - this.rollingWindow;
    return chartSeries.map((series) => ({
      ...series,
      data: this.convertDataToLC(series.data.filter((point) => {
        if (!this.realtimeMode || this.rollingWindow <= 0) return true;
        const ms = typeof point.x === 'number' ? point.x : new Date(point.x).getTime();
        return ms > cutoffTime;
      })).map((point) => ({
        x: (point.time as number) * 1000,
        y: point.value,
      })),
    }));
  }

  private getRenderedSeriesData(api: ISeriesApi<any>): IRealtimeChartPoint[] {
    return api.data()
      .filter((point): point is { time: UTCTimestamp; value: number } => 'value' in point)
      .map((point) => ({ time: point.time as number, value: point.value }));
  }

  private colorToRgba(color: string, alpha: number): string {
    if (/^#[0-9a-fA-F]{6}$/.test(color)) {
      return hexToRgba(color, alpha);
    }
    if (/^#[0-9a-fA-F]{3}$/.test(color)) {
      const expanded = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
      return hexToRgba(expanded, alpha);
    }
    const match = color.match(/hsl\(([^)]+)\)/);
    if (!match) return color;
    const parts = match[1].trim().split(/\s+/);
    if (parts.length < 3) return color;
    const h = parseFloat(parts[0]) / 360;
    const s = parseFloat(parts[1]) / 100;
    const l = parseFloat(parts[2]) / 100;
    let r: number, g: number, b: number;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q2 = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p2 = 2 * l - q2;
      r = hue2rgb(p2, q2, h + 1/3);
      g = hue2rgb(p2, q2, h);
      b = hue2rgb(p2, q2, h - 1/3);
    }
    return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`;
  }

  private getSeriesColors(isDark: boolean) {
    // Shared catalog palette so the same index resolves to the same color
    // in dees-chart-donut and every other echarts-based chart.
    return getEchartsSeriesColors(!isDark);
  }

  private resolveSeriesColor(entry: { color?: string } | undefined, index: number, isDark: boolean): string {
    if (entry?.color) return entry.color;
    const palette = this.getSeriesColors(isDark);
    return palette[index % palette.length];
  }

  private applyTheme() {
    if (!this.chart || !this.lcBundle) return;
    const isDark = !this.goBright;

    this.chart.applyOptions({
      layout: {
        background: { type: this.lcBundle.ColorType.Solid as any, color: 'transparent' },
        textColor: isDark ? 'hsl(0 0% 63.9%)' : 'hsl(0 0% 20%)',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: isDark ? 'hsl(0 0% 8.5%)' : 'hsl(0 0% 95.5%)' },
        horzLines: { color: isDark ? 'hsl(0 0% 8.5%)' : 'hsl(0 0% 95.5%)' },
      },
    } as any);

    let idx = 0;
    for (const [, api] of this.seriesApis) {
      const color = this.resolveSeriesColor(this.chartSeries[idx], idx, isDark);
      api.applyOptions({
        topColor: this.colorToRgba(color, isDark ? 0.4 : 0.5),
        bottomColor: this.colorToRgba(color, 0),
        lineColor: color,
      });
      idx++;
    }
  }

  private recreateSeries(chartSeries: ChartSeriesConfig) {
    if (!this.chart || !this.lcBundle) return;
    const isDark = !this.goBright;

    for (const [, api] of this.seriesApis) {
      this.chart.removeSeries(api);
    }
    this.seriesApis.clear();
    this.priceLines.clear();

    chartSeries.forEach((s, index) => {
      const color = this.resolveSeriesColor(s, index, isDark);
      const api = this.chart!.addSeries(this.lcBundle!.AreaSeries, {
        topColor: this.colorToRgba(color, isDark ? 0.4 : 0.5),
        bottomColor: this.colorToRgba(color, 0),
        lineColor: color,
        lineWidth: 2,
        lineType: this.lcBundle!.LineType.Curved,
        lastValueVisible: false,
        priceLineVisible: false,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 4,
      });

      api.setData(this.convertDataToLC(s.data));
      this.updatePriceLines(this.getSeriesKey(s, index), api, s.data, color);

      if (this.yAxisScaling !== 'dynamic') {
        api.applyOptions({
          autoscaleInfoProvider: () => ({
            priceRange: { minValue: 0, maxValue: this.yAxisMax },
          }),
        } as any);
      }

      this.seriesApis.set(this.getSeriesKey(s, index), api);
    });
    this.computeStats(chartSeries);
    this.pendingRealtimePrune = false;

    if (this.realtimeMode && this.rollingWindow > 0) {
      void this.updateTimeWindow();
    } else {
      this.chart.timeScale().fitContent();
    }
  }

  private updateSeriesPresentation(chartSeries: ChartSeriesConfig) {
    const isDark = !this.goBright;
    chartSeries.forEach((series, index) => {
      const seriesKey = this.getSeriesKey(series, index);
      const api = this.seriesApis.get(seriesKey);
      if (!api) return;
      const color = this.resolveSeriesColor(series, index, isDark);
      api.applyOptions({
        topColor: this.colorToRgba(color, isDark ? 0.4 : 0.5),
        bottomColor: this.colorToRgba(color, 0),
        lineColor: color,
      });
      this.updatePriceLines(seriesKey, api, series.data, color);
    });
    this.computeStats(chartSeries);

    if (this.yAxisScaling === 'dynamic') {
      const allValues = chartSeries.flatMap((series) => series.data.map((point) => point.y));
      if (allValues.length > 0) {
        const dynamicMax = Math.ceil(Math.max(...allValues) * 1.1);
        for (const [, api] of this.seriesApis) {
          api.applyOptions({
            autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: dynamicMax } }),
          } as any);
        }
      }
    }
  }

  private handleCrosshairCleared() {
    this.crosshairActive = false;
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
    this.flushPendingRealtimePrune();
  }

  private clearCrosshairForDataReplacement() {
    this.pendingRealtimePrune = false;
    this.chart?.clearCrosshairPosition();
    this.crosshairActive = false;
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
  }

  private flushPendingRealtimePrune() {
    if (
      !this.pendingRealtimePrune
      || this.crosshairActive
      || !this.chart
      || !this.realtimeMode
      || this.rollingWindow <= 0
    ) return;

    const canonicalSeries = this.getCanonicalChartSeries(this.internalChartData);
    canonicalSeries.forEach((series, index) => {
      const api = this.seriesApis.get(this.getSeriesKey(series, index));
      if (api) api.setData(this.convertDataToLC(series.data));
    });
    this.pendingRealtimePrune = false;
  }

  private computeStats(chartSeries: ChartSeriesConfig) {
    const isDark = !this.goBright;
    this.seriesStats = chartSeries.map((s, index) => {
      const color = this.resolveSeriesColor(s, index, isDark);
      const values = s.data.map(d => d.y);
      if (values.length === 0) {
        return { name: s.name || `series-${index}`, latest: 0, min: 0, max: 0, avg: 0, color };
      }
      const latest = values[values.length - 1];
      const min = Math.min(...values);
      const max = Math.max(...values);
      const avg = Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 100) / 100;
      return { name: s.name || `series-${index}`, latest, min, max, avg, color };
    });
  }

  private updatePriceLines(name: string, api: ISeriesApi<any>, data: Array<{ x: any; y: number }>, color: string) {
    // Remove existing price lines for this series
    const existing = this.priceLines.get(name);
    if (existing) {
      for (const line of existing) {
        api.removePriceLine(line);
      }
    }

    if (data.length === 0) {
      this.priceLines.set(name, []);
      return;
    }

    const values = data.map(d => d.y);
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
    const max = Math.max(...values);

    const lines: any[] = [];

    if (this.chartLines.includes('avg')) {
      lines.push(api.createPriceLine({
        price: Math.round(avg * 100) / 100,
        color: this.colorToRgba(color, 0.5),
        lineWidth: 1,
        lineStyle: 2, // Dashed
        axisLabelVisible: true,
        title: 'avg',
      }));
    }

    // Only show max line if it's more than 10% above average
    if (this.chartLines.includes('max') && avg > 0 && (max - avg) / avg > 0.1) {
      lines.push(api.createPriceLine({
        price: max,
        color: this.colorToRgba(color, 0.8),
        lineWidth: 1,
        lineStyle: 1, // Dotted
        axisLabelVisible: true,
        title: 'max',
      }));
    }

    this.priceLines.set(name, lines);
  }

  private setupTooltip() {
    if (!this.chart) return;

    this.tooltipEl = document.createElement('div');
    this.tooltipEl.className = 'lw-tooltip';
    this.tooltipEl.style.display = 'none';
    this.chartContainer = this.shadowRoot!.querySelector('.chartContainer') as HTMLDivElement | null;
    this.chartContainer?.appendChild(this.tooltipEl);
    this.chartContainer?.addEventListener('mouseleave', this.chartMouseLeaveHandler);

    this.crosshairMoveHandler = (param: MouseEventParams) => {
      if (!this.tooltipEl) return;

      if (!param.point || param.time === undefined || param.point.x < 0 || param.point.y < 0) {
        this.handleCrosshairCleared();
        return;
      }
      this.crosshairActive = true;

      const isDark = !this.goBright;
      const bgColor = isDark ? 'hsl(0 0% 9%)' : 'hsl(0 0% 100%)';
      const textColor = isDark ? 'hsl(0 0% 95%)' : 'hsl(0 0% 9%)';
      const borderColor = isDark ? 'hsl(0 0% 14.9%)' : 'hsl(0 0% 89.8%)';

      let html = '';
      let idx = 0;
      let hasData = false;
      for (const api of this.seriesApis.values()) {
        const data = param.seriesData.get(api);
        if (data && 'value' in data && (data as any).value !== undefined) {
          hasData = true;
          const series = this.internalChartData[idx];
          const name = series ? this.getSeriesName(series, idx) : `series-${idx}`;
          const color = this.resolveSeriesColor(series, idx, isDark);
          const formatted = this.yAxisFormatter((data as any).value);
          html += `<div style="display:flex;align-items:center;gap:8px;margin:${idx > 0 ? '6px' : '0'} 0;">
            <span style="display:inline-block;width:10px;height:10px;background:${color};border-radius:2px;"></span>
            <span style="font-weight:500;">${name}:</span>
            <span style="margin-left:auto;font-weight:600;">${formatted}</span>
          </div>`;
        }
        idx++;
      }

      if (!hasData) {
        this.tooltipEl.style.display = 'none';
        return;
      }

      this.tooltipEl.innerHTML = html;
      Object.assign(this.tooltipEl.style, {
        display: 'block',
        background: bgColor,
        color: textColor,
        borderColor: borderColor,
      });

      const containerWidth = this.shadowRoot!.querySelector('.chartContainer')!.clientWidth;
      let left = param.point.x + 16;
      if (left + 200 > containerWidth) left = param.point.x - 216;
      this.tooltipEl.style.left = `${left}px`;
      this.tooltipEl.style.top = `${param.point.y - 16}px`;
    };
    this.chart.subscribeCrosshairMove(this.crosshairMoveHandler);
  }

  // --- Lifecycle ---

  public async firstUpdated() {
    await this.domtoolsPromise;
    if (!this.isConnected) return;
    this.lcBundle = await DeesServiceLibLoader.getInstance().loadLightweightCharts();
    if (!this.isConnected) return;
    await new Promise(resolve => requestAnimationFrame(resolve));
    if (!this.isConnected) return;

    const chartContainer = this.shadowRoot!.querySelector('.chartContainer') as HTMLDivElement;
    if (!chartContainer) return;

    const isDark = !this.goBright;

    try {
      this.chart = this.lcBundle.createChart(chartContainer, {
        autoSize: true,
        layout: {
          background: { type: this.lcBundle.ColorType.Solid as any, color: 'transparent' },
          textColor: isDark ? 'hsl(0 0% 63.9%)' : 'hsl(0 0% 20%)',
          fontFamily: 'system-ui, "Segoe UI", sans-serif',
          fontSize: 12,
          attributionLogo: false,
        },
        grid: {
          vertLines: { color: isDark ? 'hsl(0 0% 8.5%)' : 'hsl(0 0% 95.5%)' },
          horzLines: { color: isDark ? 'hsl(0 0% 8.5%)' : 'hsl(0 0% 95.5%)' },
        },
        rightPriceScale: {
          borderVisible: false,
          scaleMargins: { top: 0.1, bottom: 0.1 },
        },
        timeScale: {
          borderVisible: false,
          timeVisible: true,
          secondsVisible: true,
        },
        crosshair: {
          mode: this.lcBundle.CrosshairMode.Magnet,
        },
        handleScroll: false,
        handleScale: false,
      } as any);

      const chartSeries = this.series.length > 0 ? this.series : [
        {
          name: 'cpu',
          data: [
            { x: '2025-01-15T03:00:00', y: 25 },
            { x: '2025-01-15T07:00:00', y: 30 },
            { x: '2025-01-15T11:00:00', y: 20 },
            { x: '2025-01-15T15:00:00', y: 35 },
            { x: '2025-01-15T19:00:00', y: 25 },
          ],
        },
        {
          name: 'memory',
          data: [
            { x: '2025-01-15T03:00:00', y: 10 },
            { x: '2025-01-15T07:00:00', y: 12 },
            { x: '2025-01-15T11:00:00', y: 10 },
            { x: '2025-01-15T15:00:00', y: 30 },
            { x: '2025-01-15T19:00:00', y: 40 },
          ],
        },
      ];

      const canonicalSeries = this.getCanonicalChartSeries(chartSeries);
      this.internalChartData = canonicalSeries;
      this.recreateSeries(canonicalSeries);
      this.setupTooltip();
      this.setupRangeSelection();
      this.syncAutoScroll();
    } catch (error) {
      console.error('Failed to initialize chart:', error);
    }
  }

  public async updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);

    if (changedProperties.has('goBright') && this.chart) {
      this.applyTheme();
    }

    const seriesChanged = changedProperties.has('series') && this.series.length > 0;
    if (seriesChanged && this.chart) {
      await this.updateSeries(this.series);
    }

    if (changedProperties.has('yAxisFormatter') && this.chart) {
      // yAxisFormatter is used by the tooltip; LC price scale uses default formatting
    }

    const realtimeConfigurationChanged = changedProperties.has('realtimeMode')
      || changedProperties.has('rollingWindow');
    const autoScrollConfigurationChanged = realtimeConfigurationChanged
      || changedProperties.has('autoScrollInterval');
    if (this.chart && realtimeConfigurationChanged) {
      if (!seriesChanged && this.internalChartData.length > 0) {
        await this.updateSeries(this.internalChartData);
      }
      if (this.realtimeMode && this.rollingWindow > 0) {
        await this.updateTimeWindow();
      } else {
        this.chart.timeScale().fitContent();
      }
    }
    if (this.chart && autoScrollConfigurationChanged) {
      this.syncAutoScroll();
    }

    if (
      changedProperties.has('selectedRange')
      || changedProperties.has('rangeSelectionEnabled')
    ) {
      this.syncRangeOverlay();
    }

    if (changedProperties.has('chartLines') && this.chart) {
      this.refreshPriceLines();
    }

    if ((changedProperties.has('yAxisScaling') || changedProperties.has('yAxisMax')) && this.chart) {
      for (const [, api] of this.seriesApis) {
        if (this.yAxisScaling === 'dynamic') {
          api.applyOptions({ autoscaleInfoProvider: undefined } as any);
        } else {
          api.applyOptions({
            autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: this.yAxisMax } }),
          } as any);
        }
      }
    }
  }

  // --- Public API ---

  public async updateSeries(newSeries: ChartSeriesConfig, animate: boolean = true) {
    if (!this.chart) return;

    try {
      void animate;
      const canonicalSeries = this.getCanonicalChartSeries(newSeries);
      this.internalChartData = canonicalSeries;
      const renderedKeys = [...this.seriesApis.keys()];
      const canonicalKeys = canonicalSeries.map((series, index) => this.getSeriesKey(series, index));

      if (!haveSameSeriesLayout(renderedKeys, canonicalKeys)) {
        this.clearCrosshairForDataReplacement();
        this.recreateSeries(canonicalSeries);
        this.updateSeriesPresentation(canonicalSeries);
        return;
      }

      if (!this.realtimeMode || this.rollingWindow <= 0) {
        this.clearCrosshairForDataReplacement();
        canonicalSeries.forEach((series, index) => {
          this.seriesApis.get(this.getSeriesKey(series, index))!
            .setData(this.convertDataToLC(series.data));
        });
        this.pendingRealtimePrune = false;
        this.updateSeriesPresentation(canonicalSeries);
        return;
      }

      const plannedUpdates = canonicalSeries.map((series, index) => {
        const api = this.seriesApis.get(this.getSeriesKey(series, index))!;
        const canonicalData = this.convertDataToLC(series.data)
          .map((point) => ({ time: point.time as number, value: point.value }));
        return {
          api,
          canonicalData,
          plan: planRealtimeChartUpdates(this.getRenderedSeriesData(api), canonicalData),
        };
      });

      if (plannedUpdates.some(({ plan }) => plan.replacementRequired)) {
        this.clearCrosshairForDataReplacement();
        for (const { api, canonicalData } of plannedUpdates) {
          api.setData(canonicalData.map((point) => ({
            time: point.time as UTCTimestamp,
            value: point.value,
          })));
        }
        this.pendingRealtimePrune = false;
      } else {
        for (const { api, plan } of plannedUpdates) {
          for (const update of plan.updates) {
            api.update({
              time: update.point.time as UTCTimestamp,
              value: update.point.value,
            }, update.historicalUpdate);
          }
        }

        const pruneRequired = plannedUpdates.some(({ plan }) => plan.pruneRequired);
        const pruneRecommended = plannedUpdates.some(({ plan }) => plan.pruneRecommended);
        if (pruneRequired) {
          this.clearCrosshairForDataReplacement();
          for (const { api, canonicalData } of plannedUpdates) {
            api.setData(canonicalData.map((point) => ({
              time: point.time as UTCTimestamp,
              value: point.value,
            })));
          }
        } else if (pruneRecommended && this.crosshairActive) {
          this.pendingRealtimePrune = true;
        } else if (pruneRecommended) {
          for (const { api, canonicalData } of plannedUpdates) {
            api.setData(canonicalData.map((point) => ({
              time: point.time as UTCTimestamp,
              value: point.value,
            })));
          }
          this.pendingRealtimePrune = false;
        }
      }

      this.updateSeriesPresentation(canonicalSeries);
    } catch (error) {
      console.error('Failed to update chart series:', error);
    }
  }

  public async updateTimeWindow() {
    if (!this.chart || this.rollingWindow <= 0) return;
    const now = Date.now();
    const cutoffTime = now - this.rollingWindow;
    try {
      this.chart.timeScale().setVisibleRange({
        from: Math.floor(cutoffTime / 1000) as UTCTimestamp,
        to: Math.floor(now / 1000) as UTCTimestamp,
      });
    } catch (e) { /* range may be invalid */ }
  }

  public async appendData(newData: { name?: string; data: Array<{ x: any; y: number }> }[]) {
    if (!this.chart) return;
    const nextSeries = this.internalChartData.map((series) => ({
      ...series,
      data: [...series.data],
    }));
    const namedOccurrences = new Map<string, number>();
    newData.forEach((s, index) => {
      let targetIndex = index;
      if (s.name) {
        const occurrence = namedOccurrences.get(s.name) ?? 0;
        namedOccurrences.set(s.name, occurrence + 1);
        const matchingIndices = this.internalChartData
          .map((series, seriesIndex) => series.name === s.name ? seriesIndex : -1)
          .filter((seriesIndex) => seriesIndex >= 0);
        const matchingIndex = matchingIndices[occurrence];
        if (matchingIndex === undefined) return;
        targetIndex = matchingIndex;
      }
      const targetSeries = nextSeries[targetIndex];
      if (!targetSeries || s.data.length === 0) return;
      targetSeries.data.push(...s.data);
    });
    await this.updateSeries(nextSeries);
  }

  public async updateOptions(options: Record<string, any>) {
    if (!this.chart) return;
    this.chart.applyOptions(options as any);
  }

  public async resizeChart() {
    // With autoSize: true, LC handles resizing automatically.
    // This method is kept for API compatibility.
    if (this.chart) {
      this.chart.timeScale().fitContent();
    }
  }

  public async forceResize() {
    await this.resizeChart();
  }

  public toggleFullPage() {
    this.isFullPage = !this.isFullPage;
    if (this.isFullPage) {
      this.style.position = 'fixed';
      this.style.inset = '0';
      this.style.zIndex = '10000';
      this.style.height = '100vh';
      this.style.padding = '0';
      document.body.style.overflow = 'hidden';
    } else {
      this.style.position = '';
      this.style.inset = '';
      this.style.zIndex = '';
      this.style.height = '';
      this.style.padding = '';
      document.body.style.overflow = '';
    }
    // Give LC a tick to resize
    requestAnimationFrame(() => {
      this.chart?.timeScale().fitContent();
    });
  }

  private refreshPriceLines() {
    const isDark = !this.goBright;
    this.getCanonicalChartSeries(this.internalChartData).forEach((s, index) => {
      const seriesKey = this.getSeriesKey(s, index);
      const api = this.seriesApis.get(seriesKey);
      if (!api) return;
      this.updatePriceLines(seriesKey, api, s.data, this.resolveSeriesColor(s, index, isDark));
    });
  }

  private startAutoScroll() {
    if (this.autoScrollTimer !== null) return;
    this.autoScrollTimer = window.setInterval(() => {
      this.updateTimeWindow();
    }, this.autoScrollInterval);
  }

  private stopAutoScroll() {
    if (this.autoScrollTimer !== null) {
      window.clearInterval(this.autoScrollTimer);
      this.autoScrollTimer = null;
    }
  }

  private syncAutoScroll() {
    this.stopAutoScroll();
    if (this.realtimeMode && this.rollingWindow > 0 && this.autoScrollInterval > 0) {
      this.startAutoScroll();
    }
  }
}
